import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

import {
  MIN_SCAN_FRAME_BYTES,
  validateScanFrameCoverage,
} from "./scan-frame-rules";

/** Äldre signerad filmuppladdning. Behålls för redan startade klienter/köjobb. */
export const createScanUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({ mimeType: z.string().min(1).max(80), sizeBytes: z.number().int().positive() })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { readPlanAndUsage } = await import("./plans.server");
    const { limitsFor } = await import("./plans");
    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    if (!limitsFor(plan, unlimited).video) {
      const { readTrialStatus } = await import("./trial.server");
      const trial = await readTrialStatus(context.userId, plan, unlimited);
      if (trial.videoScansLeft <= 0) {
        throw new Error("Videoskanning ingår i Pro. Uppgradera för att använda den.");
      }
    }
    if (!/^video\//i.test(data.mimeType)) throw new Error("Endast videofiler tillåts.");
    const { SCAN_MAX_BYTES } = await import("./video-scan");
    if (data.sizeBytes > SCAN_MAX_BYTES) throw new Error("Den äldre filmuppladdningen är för stor.");

    const { generateSlug } = await import("./ads.server");
    const ext = (data.mimeType.split("/")[1] || "mp4").replace(/[^a-z0-9]/gi, "");
    const path = `scans/${context.userId}/${Date.now()}-${generateSlug()}.${ext}`;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await supabaseAdmin.storage
      .from("ad-images")
      .createSignedUploadUrl(path);
    if (error || !signed) throw new Error("Kunde inte förbereda uppladdningen.");

    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });

/** Signerade uppladdningar för stillbilder ur filmen (görs på telefonen). */
export const createScanFrameUploads = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ count: z.number().int().min(1).max(120) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const stamp = Date.now();
    const uploads: { index: number; path: string; signedUrl: string }[] = [];
    for (let i = 0; i < data.count; i += 1) {
      const path = `scans/${context.userId}/frames/${stamp}-${i}.jpg`;
      const { data: signed } = await supabaseAdmin.storage
        .from("ad-images")
        .createSignedUploadUrl(path, { upsert: true });
      if (signed) uploads.push({ index: i, path, signedUrl: signed.signedUrl });
    }
    return { uploads };
  });

/** Startar ett beständigt manifest innan telefonen börjar läsa bildrutor. */
export const createVideoScanSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        sourceFilename: z.string().max(200).default(""),
        durationSeconds: z.number().min(0).max(3600),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { readPlanAndUsage } = await import("./plans.server");
    const { limitsFor } = await import("./plans");
    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    if (!limitsFor(plan, unlimited).video) {
      const { readTrialStatus } = await import("./trial.server");
      const trial = await readTrialStatus(context.userId, plan, unlimited);
      if (trial.videoScansLeft <= 0) {
        throw new Error("Videoskanning ingår i Pro. Uppgradera för att använda den.");
      }
    }
    const { data: session, error } = await context.supabase
      .from("video_scan_sessions")
      .insert({
        user_id: context.userId,
        source_filename: data.sourceFilename,
        duration_seconds: data.durationSeconds,
        status: "extracting",
      })
      .select("id")
      .single();
    if (error || !session) throw new Error("Kunde inte starta videoskanningen.");
    return { sessionId: session.id };
  });

/** Checkpointar varje sparad scenbild så avbrott inte raderar redan utfört arbete. */
export const checkpointVideoScanFrame = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        sessionId: z.string().uuid(),
        frame: z.object({
          path: z.string().min(1).max(300),
          atSecond: z.number().min(0).max(3600),
          sceneIndex: z.number().int().min(0).max(1000),
          sharpness: z.number().min(0).max(10000),
        }),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    if (!data.frame.path.startsWith(`scans/${context.userId}/frames/`)) {
      throw new Error("Bildrutan är ogiltig.");
    }
    const { data: row, error } = await context.supabase
      .from("video_scan_sessions")
      .select("frame_manifest")
      .eq("id", data.sessionId)
      .eq("user_id", context.userId)
      .single();
    if (error || !row) throw new Error("Skanningssessionen hittades inte.");
    const manifest = Array.isArray(row.frame_manifest) ? row.frame_manifest : [];
    const next = [...manifest, data.frame];
    const { error: updateError } = await context.supabase
      .from("video_scan_sessions")
      .update({
        frame_manifest: next,
        extracted_count: next.length,
        uploaded_count: next.length,
        scene_count: Math.max(...next.map((entry) => Number((entry as { sceneIndex?: number }).sceneIndex ?? 0))) + 1,
      })
      .eq("id", data.sessionId)
      .eq("user_id", context.userId);
    if (updateError) throw new Error("Kunde inte spara skanningens framsteg.");
    return { savedCount: next.length };
  });

export const reportVideoScanFailure = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({
    sessionId: z.string().uuid(),
    diagnostic: z.object({
      stage: z.string().min(1).max(40), code: z.string().min(1).max(100),
      name: z.string().max(100), message: z.string().max(2000), userAgent: z.string().max(1000),
      file: z.object({ name: z.string().max(200), type: z.string().max(100), bytes: z.number().int().nonnegative() }),
      at: z.string().datetime(),
    }),
    fatal: z.boolean().default(true),
  }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("video_scan_sessions")
      .update(data.fatal
        ? { status: "failed", error: JSON.stringify(data.diagnostic) }
        : { error: JSON.stringify({ ...data.diagnostic, recovered: true }) })
      .eq("id", data.sessionId).eq("user_id", context.userId);
    if (error) throw new Error("Kunde inte spara videoskanningens fellogg.");
    return { saved: true };
  });

/** Lägger en videoskanning i bakgrundskön. Resultatet kommer som en notis. */
export const scanVideo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        parts: z
          .array(
            z.object({
              path: z.string().min(1).max(300),
              startSeconds: z.number().min(0).max(3600).default(0),
            }),
          )
          .min(0)
          .max(6),
        frames: z
          .array(
            z.object({
              path: z.string().min(1).max(300),
              atSecond: z.number().min(0).max(3600),
              sceneIndex: z.number().int().min(0).max(1000).optional(),
              sharpness: z.number().min(0).max(10000).optional(),
            }),
          )
          .min(1)
          .max(120),
        sourceFilename: z.string().max(200).default(""),
        durationSeconds: z.number().min(0).max(3600).default(0),
        sessionId: z.string().uuid().optional(),
        clientDebug: z
          .object({
            startedAt: z.string().datetime(),
            uploadCompletedAt: z.string().datetime(),
            originalBytes: z.number().int().nonnegative(),
            originalType: z.string().max(100),
            compressMs: z.number().int().nonnegative().nullable(),
            totalCompressedBytes: z.number().int().nonnegative().nullable(),
            parts: z.array(z.object({
              index: z.number().int().nonnegative(),
              startSeconds: z.number().nonnegative(),
              endSeconds: z.number().nonnegative(),
              bytes: z.number().int().nonnegative(),
              resolution: z.string().max(40),
              compressMs: z.number().int().nonnegative(),
              uploadMs: z.number().int().nonnegative().nullable(),
            })).max(12),
            frames: z.array(z.object({
              atSecond: z.number().nonnegative(),
              sceneIndex: z.number().int().nonnegative(),
              sharpness: z.number().nonnegative(),
            })).max(120),
          })
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { readPlanAndUsage } = await import("./plans.server");
    const { limitsFor } = await import("./plans");
    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    if (!limitsFor(plan, unlimited).video) {
      const { readTrialStatus } = await import("./trial.server");
      const trial = await readTrialStatus(context.userId, plan, unlimited);
      if (trial.videoScansLeft <= 0) {
        throw new Error("Videoskanning ingår i Pro. Uppgradera för att använda den.");
      }
    }

    // Bara sina egna uppladdningar får skannas.
    for (const part of data.parts) {
      if (!part.path.startsWith(`scans/${context.userId}/`)) {
        throw new Error("Filmen kunde inte hittas.");
      }
    }
    const frames = data.frames.filter((f) =>
      f.path.startsWith(`scans/${context.userId}/frames/`),
    );
    if (frames.length !== data.frames.length) throw new Error("En eller flera bildrutor är ogiltiga.");
    // Hybridflödet analyserar filmen. Bilderna är representativa UI-bilder och
    // behöver därför inte täcka var åttonde sekund som det äldre bild-AI-flödet.
    if (!data.parts.length) {
      const coverage = validateScanFrameCoverage(frames, data.durationSeconds);
      if (!coverage.ok) throw new Error(coverage.message);
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    for (const frame of frames) {
      const { data: jpeg, error: jpegError } = await supabaseAdmin.storage
        .from("ad-images")
        .download(frame.path);
      if (jpegError || !jpeg || jpeg.size < MIN_SCAN_FRAME_BYTES) {
        throw new Error("En bildruta ur filmen saknas eller är trasig. Prova filmen igen.");
      }
      const signature = new Uint8Array(await jpeg.slice(0, 3).arrayBuffer());
      if (signature[0] !== 0xff || signature[1] !== 0xd8 || signature[2] !== 0xff) {
        throw new Error("En bildruta ur filmen är inte en giltig JPEG. Prova filmen igen.");
      }
    }

    const { enforceRateLimit } = await import("./rate-limit.server");
    await enforceRateLimit("video_scan", context.userId);

    const { enqueueJob, countActiveJobs, kickQueue } = await import("./job-queue.server");
    if ((await countActiveJobs(context.userId)) >= 3) {
      throw new Error("Du har redan tre skanningar i kön. Vänta tills någon blir klar.");
    }
    const kind = data.parts.length ? "video_scan" : "video_frame_scan";
    const { jobId } = await enqueueJob({
      userId: context.userId,
      plan: unlimited ? "expert" : plan,
      kind,
      label: data.sourceFilename || "Videoskanning",
      payload: {
        parts: data.parts,
        frames,
        sourceFilename: data.sourceFilename,
        durationSeconds: data.durationSeconds,
        sessionId: data.sessionId ?? null,
        clientDebug: data.clientDebug ?? null,
      },
    });
    if (data.sessionId) {
      await context.supabase
        .from("video_scan_sessions")
        .update({ status: "queued", job_id: jobId })
        .eq("id", data.sessionId)
        .eq("user_id", context.userId);
    }
    await kickQueue();
    return { jobId };
  });


/** Hur många skanningar användaren har kvar den här timmen och det här dygnet. */
export const getScanQuota = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { getRateStatus } = await import("./rate-limit.server");
    return await getRateStatus("video_scan", context.userId);
  });
